import { formatEuros, VERHUURTYPES } from "@bivak/domain";
import { useQuery } from "@tanstack/react-query";
import { nl } from "../../copy/nl.js";
import { fetchTarieven, type Tarief } from "../../lib/api.js";
import { asCents } from "../../lib/format.js";

/** Straight from the SAME price_rule rows the quote uses (0.25): the card can
 *  never contradict the quote, because neither has its own copy anymore. */
function priceLines(tarief: Tarief): string[] {
  const lines: string[] = [];
  if (tarief.perDayCents !== null) {
    lines.push(nl.kalender.prijsPerDag(formatEuros(asCents(tarief.perDayCents))));
  }
  if (tarief.perPersonPerNightCents !== null) {
    lines.push(
      nl.kalender.prijsPerPersoonNacht(formatEuros(asCents(tarief.perPersonPerNightCents))),
    );
  }
  if (tarief.minimumCents > 0) {
    lines.push(nl.kalender.prijsMinimum(formatEuros(asCents(tarief.minimumCents))));
  }
  return lines;
}

/**
 * The three formulas side by side, subscription-card style: the renter's first
 * question is "which kind of stay", not "which room". These cards are also the
 * tariff display — there is no separate tariff page or table.
 */
export function VerhuurtypeKaarten() {
  const tarieven = useQuery({
    queryKey: ["tarieven"],
    queryFn: fetchTarieven,
    staleTime: 300_000,
  });

  return (
    // Deliberately tight: on a phone the three cards stack, and every spare
    // pixel here pushes the calendar — the page's actual subject — further
    // down (design review + user, 2026-08-21).
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {VERHUURTYPES.map((type) => {
        const kaart = nl.kalender.types[type];
        const tarief = tarieven.data?.tarieven.find((kandidaat) => kandidaat.verhuurtype === type);
        const [hoofdprijs, ...rest] = tarief ? priceLines(tarief) : [];
        return (
          <section
            key={type}
            className="flex flex-col rounded-2xl border-2 border-lijn bg-kaart p-4"
          >
            <span className="font-mono text-[10.5px] text-rood uppercase tracking-widest">
              {kaart.periode}
            </span>
            <h2 className="mt-0.5 font-bold font-display text-xl tracking-tight">{kaart.naam}</h2>
            {/* The price rows keep their height while loading, so the cards
                never jump; a missing tariff shows an honest dash, never a
                guessed number (same rule as the quote's ambiguous_rules). */}
            <p className="mt-1.5 min-h-6 font-bold font-mono text-[17px]">
              {hoofdprijs ?? (tarieven.isPending ? "" : "—")}
            </p>
            {rest.length > 0 && (
              <p className="font-mono text-[12px] text-mist">{rest.join(" · ")}</p>
            )}
            <ul className="mt-2.5 flex flex-col gap-1 text-[13px]">
              {kaart.troeven.map((troef) => (
                <li key={troef} className="relative pl-4">
                  <i className="absolute top-[7px] left-0 block h-1.5 w-1.5 rounded-sm bg-rood" />
                  {troef}
                </li>
              ))}
            </ul>
            {/* No button at all (user call, 2026-08-21): these cards are the
                tariff display, not a decision point — the formula follows from
                the dates, and the one CTA lives in the selection bar. */}
          </section>
        );
      })}
    </div>
  );
}
